import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarClock, MapPin, IndianRupee, Phone, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BACKEND_URL } from "@/lib/api";

const STATUS_COLORS = {
  pending_payment: "bg-brand-yellow/20 text-brand-ink",
  confirmed: "bg-brand-cyan/20 text-brand-ink",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-muted text-muted-foreground",
};

export default function ProviderBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/bookings").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const complete = async (id) => {
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Marked completed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buckets = {
    incoming: items.filter((b) => b.status === "confirmed"),
    pending: items.filter((b) => b.status === "pending_payment"),
    completed: items.filter((b) => b.status === "completed"),
    cancelled: items.filter((b) => b.status === "cancelled"),
  };

  const Row = ({ b }) => (
    <div className="rounded-2xl border border-foreground/10 bg-white p-4" data-testid={`prov-booking-${b.id}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <img src={b.service?.image_url} alt="" className="h-20 w-full rounded-xl object-cover sm:w-28" />
        <div className="flex-1">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[b.status]}`}>
            {b.status.replace("_", " ")}
          </span>
          <div className="mt-1 font-heading text-base font-bold">{b.service?.title}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><CalendarClock size={12} /> {new Date(b.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {b.address}</span>
            <span className="flex items-center gap-1"><IndianRupee size={12} /> {b.amount}</span>
          </div>
          <div className="mt-2 text-xs">
            <span className="text-muted-foreground">Customer:</span> <b>{b.customer_info?.name}</b>
            {b.customer_info?.phone && (<span className="ml-2 inline-flex items-center gap-1 text-muted-foreground"><Phone size={10} /> {b.customer_info.phone}</span>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {b.payment_status === "paid" && (
            <a
              href={`${BACKEND_URL}/api/bookings/${b.id}/receipt`}
              target="_blank" rel="noreferrer"
              data-testid={`prov-receipt-${b.id}`}
            >
              <Button variant="outline" className="rounded-full">
                <FileDown size={14} className="mr-1.5" /> Receipt
              </Button>
            </a>
          )}
          {b.status === "confirmed" && (
            <>
              <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" onClick={() => complete(b.id)} data-testid={`complete-${b.id}`}>Mark completed</Button>
              <Button variant="outline" className="rounded-full" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}>Cancel</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="provider-bookings-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Bookings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Accept, complete or cancel jobs.</p>
      </div>

      <Tabs defaultValue="incoming">
        <TabsList>
          <TabsTrigger value="incoming" data-testid="ptab-incoming">Confirmed ({buckets.incoming.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="ptab-pending">Pending pay ({buckets.pending.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="ptab-completed">Completed ({buckets.completed.length})</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="ptab-cancelled">Cancelled ({buckets.cancelled.length})</TabsTrigger>
        </TabsList>
        {Object.entries(buckets).map(([k, v]) => (
          <TabsContent key={k} value={k} className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : v.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">Nothing here yet.</div>
            ) : v.map((b) => <Row key={b.id} b={b} />)}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
